<template>
    <v-expansion-panels multiple
        v-for="v in (Object.values(groupMap) as any[]).sort((a: any, b: any) => b.totalDamage - a.totalDamage)"
        v-bind:key="v.actor.id">
        <template v-if="v.totalDamage > 0">
            <v-expansion-panel>
                <v-expansion-panel-title>
                    <v-sheet>
                        {{ prettyEntityName(v.actor) }} {{ v.totalDamage.toFixed(0) }}
                    </v-sheet>

                </v-expansion-panel-title>
                <v-expansion-panel-text class="pa-3">
                    <template v-for="(entity, entityk) in (v.entities as any)" v-bind:key="entityk">
                        <template v-if="entity.totalDamage > 0">
                            <v-sheet>
                                * {{ prettyEntityName(entity.actor) }} {{ entity.totalDamage.toFixed(0) }}
                                {{ entity.actor.finisherId ? `Killed by
                                ${prettyEntityName(entityMap[entity.actor.finisherId]) ||
                                    entity.actor.finisherId}` : '' }}
                                <template v-for="cond in entity.actor.conditionMap" v-bind:key="cond.CCId">
                                    <img width="16" height="16"
                                        @mouseover="e => setCondTooltip(e.target! as HTMLElement, cond)"
                                        @mouseleave="e => setCondTooltip(e.target! as HTMLElement, undefined)"
                                        @click="e => setCondTooltip(e.target! as HTMLElement, cond)"
                                        :src='`/res/characterconditionimage/${region}/${cond.CCId}/${cond.CCId}.png`' />
                                </template>
                            </v-sheet>

                            <v-sheet
                                v-for="[attackerId, damageByAttacker] in Object.entries(entity.damagesByAttacker).sort(([, av], [, bv]) => (bv as any) - (av as any))"
                                v-bind:key="attackerId" width="100%" class="mb-4">
                                <!-- 이름 -->
                                <v-sheet width="100%">
                                    {{ prettyEntityName(entityMap[attackerId]) || attackerId }} {{
                                        (damageByAttacker as any).toFixed(0) }} {{
                                        (100 * (damageByAttacker as any) / entity.totalDamage).toFixed(1) }}%
                                </v-sheet>

                                <!-- 막대 -->
                                <v-sheet width="100%" height="16">
                                    <v-sheet
                                        :color="getMabiNameColor(prettyEntityName(entityMap[attackerId]) || attackerId)"
                                        height="100%"
                                        :width="`${Math.round(100 * (damageByAttacker as any) / entity.totalDamage).toFixed(0)}%`"
                                        class="rounded-xl">
                                    </v-sheet>
                                </v-sheet>
                            </v-sheet>
                        </template>
                    </template>
                </v-expansion-panel-text>
            </v-expansion-panel>
            <v-sheet
                v-for="[attackerId, damageByAttacker] in Object.entries(v.damagesByAttacker).sort(([, av], [, bv]) => (bv as any) - (av as any))"
                v-bind:key="attackerId" width="100%" class="mb-4 pa-1">
                <!-- 이름 -->
                <v-sheet width="100%">
                    {{ prettyEntityName(entityMap[attackerId]) || attackerId }} {{ (damageByAttacker as any).toFixed(0) }}
                    {{
                        (100 * (damageByAttacker as any) / v.totalDamage).toFixed(1) }}%
                </v-sheet>

                <!-- 막대 -->
                <v-sheet width="100%" height="16">
                    <v-sheet 
                        :color="getMabiNameColor(prettyEntityName(entityMap[attackerId]) || attackerId)"
                        height="100%" :width="`${Math.round(100 * (damageByAttacker as any) / v.totalDamage).toFixed(0)}%`"
                        class="rounded-xl">
                    </v-sheet>
                </v-sheet>
            </v-sheet>
        </template>

    </v-expansion-panels>
    
    <v-tooltip v-if="condTooltip" v-model="condTooltipValue" :activator="condTooltipParent">
        {{ condNameMap[condTooltip.CCId] }}
    </v-tooltip>
</template>

<script lang="ts">
import { defineComponent, inject, ref, computed } from "vue";
import { getMabiNameColor } from '@/util';
import { EntityCondition, ActorManager, BaseActor, GroupActor, EntityActor } from '@/eventActor';

export default defineComponent({
    setup() {
        const isLoading = inject('isLoading');
        const region = inject('region');
        const raceNameMap = inject('raceNameMap') as any;
        const skillNameMap = inject('skillNameMap');
        const condNameMap = inject('condNameMap') as any;
        const actorManager = inject('actorManager') as any;
        const appEvent = inject('appEvent') as any;

        const entityMap = computed(() => {
            appEvent.value;
            actorManager.value.liveUpdate.value;
            return actorManager.value.entityMap as Record<string, EntityActor>;
        });

        const groupMap = computed(() => {
            appEvent.value;
            actorManager.value.liveUpdate.value;
            const m: Record<string, any> = {};

            for (const k in actorManager.value.groupMap) {
                const groupActor = actorManager.value.groupMap[k];
                let groupTotalDamage = 0;
                let groupDamagesByAttacker: Record<string, number> = {};
                let entities: Record<string, any> = {};

                for (const ek in groupActor.entityMap) {
                    const e = actorManager.value.entityMap[ek] as any;
                    if (!e) continue;
                    let eTotalDamage = e._totalTakeDamage || 0;
                    let eDamagesByAttacker: Record<string, number> = {};

                    if (e.aggregatedTakeStats) {
                        for (const attackerId in e.aggregatedTakeStats) {
                            const stats = e.aggregatedTakeStats[attackerId];
                            eDamagesByAttacker[attackerId] = stats.totalDamage;
                            groupDamagesByAttacker[attackerId] = (groupDamagesByAttacker[attackerId] || 0) + stats.totalDamage;
                        }
                    }

                    groupTotalDamage += eTotalDamage;
                    
                    entities[ek] = {
                        actor: e,
                        totalDamage: eTotalDamage,
                        damagesByAttacker: eDamagesByAttacker
                    };
                }

                m[k] = {
                    actor: groupActor,
                    totalDamage: groupTotalDamage,
                    damagesByAttacker: groupDamagesByAttacker,
                    entities: entities
                };
            }

            return m;
        });

        const condTooltipParent = ref<HTMLElement>();
        const condTooltipValue = ref(false);
        const condTooltip = ref<EntityCondition>();

        const setCondTooltip = (el: HTMLElement, cond?: EntityCondition) => {
            condTooltip.value = cond;
            condTooltipParent.value = el;
            condTooltipValue.value = !!cond;
        }

        const prettyEntityName = (entity?: BaseActor) => {
            if (!entity) return undefined;
            if (ActorManager.pcRaceSet.has(entity.raceId)) return entity.name;
            const raceName = raceNameMap.value[entity.raceId] || `unknownRace:${entity.raceId}`;
            if (entity instanceof GroupActor) return raceName;
            if (entity.name[0] >= '0' && entity.name[0] <= '9') return `${raceName} (${entity.name.slice(-4)})`;
            return entity.name;
        }

        return {
            isLoading,
            region,

            skillNameMap,
            condNameMap,
            entityMap,
            groupMap,

            condTooltip,
            condTooltipParent,
            condTooltipValue,
            setCondTooltip,

            getMabiNameColor,
            prettyEntityName,
        }
    }
});
</script>